import { ISelectItem } from "@components/inputs/select/Select.types";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy, IReportRowDef, IReportVariantDef } from "@components/smart/smartTable";
import { IFormatOptions } from "@odata/OData.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { isAccountAssignmentCompany } from "@utils/CompanyUtils";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, ConfigListItemBoundType, FieldType, Sort } from "../../../enums";
import { TValue } from "../../../global.types";
import { getActiveFiscalYear, getActiveFiscalYears, getFiscalYears } from "../../fiscalYear/FiscalYear.utils";
import { assetIntentFormatter, CommonReportProps } from "../CommonDefs";
import { IReportTableDefinition, TGetReportConfigListItemOverride } from "../Report.utils";
import { ReportId } from "../ReportIds";
import { enhanceTotalsWithGroupAssetsData, getAmountDrilldownFormatter } from "./AssetAnalysis.utils";

export enum AssetAnalysisVariant {
    Inventory = "-77",
    Deprecation = "-78"
}

export const getDefinition = (context: IAppContext): IReportTableDefinition => {
    const tableId = ReportId.AssetAnalysis;
    const title = i18next.t("Reporting:AssetAnalysis.Title");
    const path = "AssetAnalysis";
    const initialSortBy = [{ id: "JournalEntry_DateAccountingTransaction", sort: Sort.Asc }];
    const hasAccounting = isAccountAssignmentCompany(context);
    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                CommonReportProps.fiscalYearId
            ],
            filterDefinition: {
                [CommonReportProps.fiscalYearId]: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.L,
                    label: i18next.t(`Reporting:Parameters.${hasAccounting ? "FiscalYear" : "Year"}`),
                    formatter: (val, args): string => {
                        const fiscalYears = getFiscalYears(args.storage.context);
                        return fiscalYears?.find(fy => fy.Id === val)?.Number;
                    },
                    defaultValue: (args: IGetValueArgs) => {
                        const fiscalYear = getActiveFiscalYear(args.context);
                        return fiscalYear?.Id;
                    },
                    fieldSettings: {
                        itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem[]> => {
                            const fiscalYears = getActiveFiscalYears(args.context, Sort.Desc);

                            return fiscalYears.map(fiscalYear => {
                                return {
                                    id: fiscalYear.Id,
                                    label: fiscalYear.Number,
                                    additionalData: fiscalYear
                                };
                            });
                        }
                    }
                }
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {}
        }
    ];

    const parameters: string[] = [CommonReportProps.fiscalYearId];

    let deprecationCols = [
        { "ColumnAlias": "Asset_NumberOurs" },
        { "ColumnAlias": "Asset_Name" },
        { "ColumnAlias": "Asset_DateFirstPutInUse" },
        { "ColumnAlias": "Asset_CalculatedPrice" },
        { "ColumnAlias": "TaxDepreciationPolicyItemReportView_AccumulatedDepreciation" },
        { "ColumnAlias": "TaxDepreciationPolicyItemReportView_DepreciationExpense" },
        { "ColumnAlias": "TaxDepreciationPolicyItemReportView_EndValue" }
    ];

    if (hasAccounting) {
        deprecationCols = [
            ...deprecationCols,
            { "ColumnAlias": "AccountingDepreciationPolicyItemReportView_AccumulatedDepreciation" },
            { "ColumnAlias": "AccountingDepreciationPolicyItemReportView_CalculatedExpense" },
            { "ColumnAlias": "AccountingDepreciationPolicyItemReportView_EndValue" },
            { "ColumnAlias": "DepreciationDiff" },
            { "ColumnAlias": "AssetAccountBalanceReportView_Amount" }
        ];
    }

    let inventoryCols = [
        { "ColumnAlias": "Asset_NumberOurs" },
        { "ColumnAlias": "Asset_Name" },
        { "ColumnAlias": "AssetStatus_Name" },
        { "ColumnAlias": "AssetType_Name" },
        { "ColumnAlias": "Asset_DateFirstPutInUse" },
        { "ColumnAlias": "Asset_CalculatedPrice" }
    ];

    if (hasAccounting) {
        inventoryCols = [
            ...inventoryCols,
            { "ColumnAlias": "Asset_AcquisitionCostForTax" },
            { "ColumnAlias": "InUseAccount_Number" },
            { "ColumnAlias": "AccountingDepreciationPolicy_UsefulLife" },
            { "ColumnAlias": "AssetAccountBalanceReportView_Amount" }

        ];
    } else {
        inventoryCols = [
            ...inventoryCols,
            { "ColumnAlias": "TaxDepreciationPolicyItemReportView_OpeningBalance" },
            { "ColumnAlias": "TaxDepreciationPolicyItemReportView_EndValue" }
        ];
    }

    const defaultReportVariants: Record<AssetAnalysisVariant, IReportVariantDef> = {
        [AssetAnalysisVariant.Deprecation]: {
            ReportHierarchy: {
                "Aggregate": true,
                "Groups": [
                    { "ColumnAlias": "TaxDepreciationCategory_Name" }
                ],
                "Columns": deprecationCols,
                "Aggregations": []
            }
        }, [AssetAnalysisVariant.Inventory]: {
            ReportHierarchy: {
                "Aggregate": false,
                "Groups": [],
                "Columns": inventoryCols,
                "Aggregations": []
            }
        }
    };

    const defaultReportHierarchy: IReportHierarchy = defaultReportVariants[AssetAnalysisVariant.Inventory].ReportHierarchy;

    const rowsDataFactory = (rowsData: IReportRowDef[]): IReportRowDef[] => {
        enhanceTotalsWithGroupAssetsData(rowsData);

        return rowsData;
    };

    const configListItemsOverride: TGetReportConfigListItemOverride = {
        Asset_NumberOurs: {
            isRequired: true,
            boundTo: ConfigListItemBoundType.Column
        }
    };

    const columnOverrides = {
        Asset_NumberOurs: {
            formatter: assetIntentFormatter
        },
        Asset_CalculatedPrice: {
            formatter: getAmountDrilldownFormatter()
        },
        AssetAccountBalanceReportView_Amount: {
            formatter: getAmountDrilldownFormatter(true)
        },
        DepreciationSettingType_Name: {
            formatter: (val: TValue, args: IFormatOptions): string => {
                /* null means no value, undefined is there for summary rows, which should not be formatted */
                if (val === null) {
                    return i18next.t(`FixedAsset:Form.Normal`);
                }
                return val as string;
            }
        }
    };

    return {
        title, path, id: tableId, initialSortBy, filterBarDef, rowsDataFactory,
        configListItemsOverride, columnOverrides, parameters, defaultReportHierarchy, defaultReportVariants
    };
};

setReportDef(ReportId.AssetAnalysis, getDefinition);
